import axios from 'axios';
import apiService, { getToken } from './api';
import { unwrap } from '../utils/apiResponse';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface EspNowController {
  controller_id: string;
  /** Radio the controller reaches its nodes over: espnow | xbee | lora | http. */
  transport: string | null;
  organisation_id: number | null;
  controller_name: string | null;
  mac: string | null;
  ip: string | null;
  wifi_channel: number | null;
  timezone_offset: number;
  online: boolean;
  last_seen: string | null;
  farm_id: number | null;
  created_at: string;
  device_type: string | null;
  firmware_version: number | null;
  espnow_channel: number | null;
  has_local_relays: boolean | null;
  has_local_inputs: boolean | null;
  has_local_analog: boolean | null;
  local_relay_states: number | null;              // 8-bit bitmask (master's own relays)
  local_relay_state_updated_at: string | null;
  /**
   * Semver reported by an XBee laptop bridge, e.g. "0.2.0". Null for ESP-NOW
   * controllers, which report the numeric `firmware_version` instead.
   */
  bridge_version: string | null;
  /**
   * XBee sites only: is the coordinator radio attached right now?
   *
   * Distinct from `online`, which only says the laptop is talking to us. The
   * two fail independently — a bridge heartbeats perfectly happily with the USB
   * stick pulled out — and conflating them is how a farm with one unplugged
   * radio reads as a farm where every node died. `null` means the controller
   * has never reported it (every ESP-NOW master, and any bridge built before this field existed).
   */
  coordinator_connected: boolean | null;
  coordinator_last_ok: string | null;
  coordinator_error: string | null;
  /**
   * Set while watering is held at this site: nothing starts — not the nightly
   * dispatch, not the bridge's own clock, not Run now — until somebody resumes
   * it. Null is the normal state.
   */
  watering_paused_at: string | null;
  watering_pause_reason: string | null;
  router_count?: number;
  online_routers?: number;
  organisation_name?: string;
}

/** A published FactreeIrrigationBridge.exe build. */
export interface BridgeRelease {
  version: string;
  sha256: string;
  size_bytes: NumericValue;
  uploaded_at: string;
}

/**
 * The most recent UPDATE_BRIDGE command for a site.
 *
 * `retryable` is the server's reading of the bridge's ack detail, and only one
 * outcome sets it: a deferral, where the bridge held the update back because a
 * watering program was running. Everything else needs a new release or a fixed
 * command, so offering a retry button would just be a button that always fails.
 */
export interface BridgeUpdateStatus {
  cmd_id: string;
  version: string | null;
  status: 'QUEUED' | 'DISPATCHED' | 'CONFIRMED' | 'OK' | 'ERROR' | 'PENDING';
  detail: string | null;
  created_at: string;
  dispatched_at: string | null;
  confirmed_at: string | null;
  tone: 'success' | 'info' | 'error' | 'pending';
  retryable: boolean;
  summary: string;
}

export interface RelayZone {
  id: string;
  router_mac: string;
  relay_index: number;
  zone_name: string | null;
  zone_color: string;
  boundary_geometry: GeoJSONPolygon | null;
  notes: string | null;
  updated_at: string;
}

export interface LiveMapStepRelay {
  router_mac: string;
  relay_index: number;
  duration_sec: number;
}

export interface LiveMapStep {
  program_id: string;
  program_name: string;
  /**
   * Which of the program's starts this cycle is. A program that starts twice
   * a day puts every step on the timeline twice, so program + step is no
   * longer unique - key on this as well. Absent from an older server.
   */
  start_id?: string | null;
  /** That start's run length, as a percentage of the step times. */
  duration_pct?: number;
  step_id: string;
  step_order: number;
  start_sec: number;
  end_sec: number;
  relays: LiveMapStepRelay[];
  /** 1-based position in its program, as the bridge's events number it. */
  step_number?: number;
  /**
   * Where the times came from. `scheduled`: the program definition, nothing
   * reported yet. `actual`: the bridge reported this step starting or being
   * skipped. `projected`: not reached yet, timed on from what actually ran -
   * after a skip the rest of a cycle runs early. Absent from an older server.
   */
  timing?: 'scheduled' | 'actual' | 'projected';
  /**
   * The bridge skipped this sequence because a node did not answer its
   * pre-flight. Nothing opened. start_sec === end_sec, both at the skip.
   */
  skipped?: boolean;
  /** The nodes that did not answer, when skipped. */
  silent_nodes?: string[];
}

export interface LiveMapData {
  routers: EspNowRouter[];
  zones: RelayZone[];
  timeline: LiveMapStep[];
  now_sec: number;
  today_dow: number;
}

export interface MasterPump {
  id: string;
  controller_id: string;
  router_mac: string;
  relay_index: number;
  label: string | null;
  display_order: number;
  created_at: string;
}

export interface EspNowRouter {
  router_mac: string;
  controller_id: string;
  device_id: number | null;
  name: string | null;
  /** Confirmed reachable. `state` is the fuller answer — prefer it. */
  online: boolean;
  /**
   * Reachability as evidence rather than as a clock:
   *
   *   online    heard from, or answered something we asked
   *   offline   we asked and it did not answer
   *   unknown   neither, recently
   *
   * `unknown` must NOT be drawn as a fault. An XBee node with IO sampling off
   * never transmits unprompted, so silence is its resting state; showing that as
   * offline told operators a working node had failed and greyed out the controls
   * they would have used to prove otherwise. Controls stay live for an unknown
   * node — using one is how the state gets settled.
   */
  state?: 'online' | 'offline' | 'unknown' | null;
  rssi_dbm: number | null;
  relay_states: number;       // 8-bit bitmask
  relay_state_updated_at: string | null;
  lat: number | null;
  lng: number | null;
  relay_labels: Record<string, string>;
  last_seen: string | null;
  device_type: string | null;
  firmware_version: number | null;
  /** True for the master controller's own on-board relay/sensor bank. */
  is_local: boolean;
  /**
   * Capability counts advertised by the node in MSG_ANNOUNCE. null means the
   * node is on firmware predating the capability fields and has not reported
   * them — which is NOT the same as having none.
   */
  relay_count: number | null;
  di_count: number | null;
  adc_count: number | null;
  /**
   * Relays the board has but which are wired to nothing, set by an operator —
   * no node can report this. Subtractive on top of `relay_count`: a 4-relay
   * board using channels 0 and 3 still has a count of 4, with 1 and 2 listed
   * here. Read it through `usableRelays`, never raw.
   *
   * Optional because a server predating the column omits it, which means "none
   * are out of use" — the same as the empty array every row is created with.
   */
  disabled_relays?: number[] | null;
  transport: string | null;          // "lora" | "espnow" | ... (link the master reaches this router over)
  lora_ok: boolean | null;           // last LoRa exchange with this router succeeded
  lora_provisioned: boolean | null;  // router has accepted its LoRa config
}

export interface RouterRelayState {
  router_mac: string;
  device_id: number | null;
  name: string | null;
  is_local: boolean;
  relay_states: number;       // 8-bit bitmask
  relay_state_updated_at: string | null;
}

// ─── Schedule state (device-reported) ─────────────────────────────────────────

/**
 * One relay as the node itself reports holding it. This is what the hardware
 * says it will do — not what the server intended. `runnable` is computed by the
 * firmware (`enabled && duration_sec > 0 && clock_ok`); never recompute it here,
 * or the UI drifts from the device.
 */
export interface RelayScheduleState {
  relay: number;
  enabled: boolean;
  start_sec: number;          // seconds from midnight, in the NODE's own frame
  duration_sec: number;
  on: boolean;                // energised at report time
  runnable: boolean;          // will this actually water?
  reason?: string | null;     // present only when runnable is false
  next_run_in_sec?: number | null; // present only when runnable && !on
}

/** Full schedule snapshot posted by a relay-capable node (master or router). */
export interface NodeScheduleState {
  controller_id: string;
  router_mac: string;
  transport: string | null;   // "espnow" | "lora" | "local" (master's own relays)
  clock_ok: boolean;          // false ⇒ NOTHING on this node will actuate
  // The node's own seconds-from-midnight when it reported. Null when the node
  // omitted it — distinct from 0, which is a real reading of midnight.
  secs_today: number | null;
  relay_count: number;        // relays physically present on the board
  relay_states: number;       // live output bitmask
  reported_at: string | null;
  relays: RelayScheduleState[];
}

/** CONFIG_ACK event payload — see §3.2 of the hardware API guide. */
export interface ConfigAckPayload {
  router_mac?: string;
  relay_id?: number;
  status?: string;            // "OK" | "ERROR"
  status_code?: number;       // 0-4, see CONFIG_ACK_STATUS below
  detail?: string;
  armed?: boolean;            // the difference between "set" and "will water"
  cmd_id?: string;
}

/**
 * status_code → operator-facing meaning. Codes 3 and 4 still report
 * `status: "OK"` because the write succeeded — only 1 and 2 are rejections.
 * `armed: false` is the field that matters.
 */
export const CONFIG_ACK_STATUS: Record<number, { label: string; tone: 'ok' | 'warn' | 'error' }> = {
  0: { label: 'Armed — will run at start time', tone: 'ok' },
  1: { label: 'Rejected — relay number out of range', tone: 'error' },
  2: { label: 'Rejected — board has no such relay', tone: 'error' },
  3: { label: 'Stored, but node has no clock — will not run', tone: 'warn' },
  4: { label: 'Stored as disabled — duration is zero', tone: 'warn' },
};

export interface EspNowEvent {
  event_id: string;
  controller_id: string;
  router_mac: string | null;
  event_type: string;
  payload: Record<string, unknown>;
  timestamp: string;
}

export interface StepRelay {
  id: string;
  step_id: string;
  router_mac: string;
  relay_index: number;
  duration_sec: number;
  master_pump_id: string | null;
}

export interface ScheduleStep {
  id: string;
  program_id: string;
  step_order: number;
  step_duration_sec: number;
  relays: StepRelay[] | null;
}

/**
 * One time a program runs on each of its days.
 *
 * `duration_pct` is how long this run lasts as a percentage of the valve times
 * on the steps: 100 is the program as written, 50 an afternoon top-up that
 * runs every valve for half as long, in the same order.
 */
export interface ProgramStart {
  /** Absent on a start not saved yet. */
  id?: string | null;
  start_time_sec: number;   // seconds from midnight UTC, like start_time_sec
  duration_pct: number;
}

export interface ScheduleProgram {
  id: string;
  controller_id: string;
  organisation_id: number;
  name: string;
  days_of_week: number[];   // ISO weekdays: 1=Mon...7=Sun
  /**
   * The earliest start, kept by the server for bridges and app bundles that
   * predate `starts`. Read `starts` through startsOf() instead.
   */
  start_time_sec: number;   // seconds from midnight UTC
  /** Every start, earliest first. Absent from an older server. */
  starts?: ProgramStart[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
  steps: ScheduleStep[] | null;
  /**
   * The schedule as it was before the most recent "Run now", or null when the
   * program has never been forced — or has since been restored or edited by
   * hand. "Run now" is a real schedule edit (see runProgramNow), so this is
   * what makes it undoable.
   */
  previous_start_time_sec?: number | null;
  previous_days_of_week?: number[] | null;
  /** Every start as it was before the forced run, for Restore to name. */
  previous_starts?: ProgramStart[] | null;
  forced_run_at?: string | null;
}

/** What a forced run changed, so the UI can say it plainly. */
export interface RunNowResult {
  program: ScheduleProgram;
  transport: string;
  start_time_sec: number;
  previous_start_time_sec: number;
  previous_days_of_week: number[];
  /** ISO weekday added to the program's days, or null when today already was one. */
  day_added: number | null;
  /**
   * Relays that last reported ON, but too long ago for the server to refuse the
   * run over — a node that posts relay-state only on change leaves the reading
   * arbitrarily old on a perfectly healthy site, so a stale ON is *unknown*, not
   * *on*. The run went ahead; only somebody at the farm can settle it, so it has
   * to be said rather than swallowed.
   */
  unconfirmed?: Array<{
    router_mac: string;
    relay_index: number;
    label: string;
    /** Age of the reading in seconds, or null if the node has never reported. */
    state_age_sec: number | null;
  }>;
}

// ─── Alert recipients per farm ────────────────────────────────────────────────

export interface AlertRecipientUser {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  /** Irrigation seat types held now. Empty = seat lapsed, so not notified. */
  seat_types: string[];
}

export interface FarmAlertRecipients {
  farm_id: number;
  farm_name: string;
  recipients: Array<AlertRecipientUser & { notified: boolean }>;
  /** Everyone who could be added: active users holding an irrigation seat. */
  candidates: AlertRecipientUser[];
  /** True when nobody on the list is notifiable, so `fallback` is told instead. */
  using_fallback: boolean;
  /** The org's irrigation admin seat holders. */
  fallback: AlertRecipientUser[];
}

async function getFarmAlertRecipients(farmId: number): Promise<FarmAlertRecipients> {
  const res = await apiService.get<FarmAlertRecipients>(`/espnow/farms/${farmId}/alert-recipients`);
  return unwrap(res);
}

async function setFarmAlertRecipients(farmId: number, userIds: number[]): Promise<FarmAlertRecipients> {
  const res = await apiService.put<FarmAlertRecipients>(`/espnow/farms/${farmId}/alert-recipients`, {
    user_ids: userIds,
  });
  return unwrap(res);
}

// ─── Controller endpoints ─────────────────────────────────────────────────────

async function getControllers(): Promise<EspNowController[]> {
  const res = await apiService.get<EspNowController[]>('/espnow/controllers');
  return unwrap(res);
}

async function updateController(
  controllerId: string,
  data: { controller_name?: string }
): Promise<EspNowController> {
  const res = await apiService.put<EspNowController>(`/espnow/controllers/${controllerId}`, data);
  return unwrap(res);
}

// ─── Master pump endpoints ────────────────────────────────────────────────────

async function getMasterPumps(controllerId: string): Promise<MasterPump[]> {
  const res = await apiService.get<MasterPump[]>(`/espnow/controllers/${controllerId}/master-pumps`);
  return unwrap(res);
}

async function addMasterPump(
  controllerId: string,
  data: { router_mac: string; relay_index: number; label?: string | null }
): Promise<MasterPump> {
  const res = await apiService.post<MasterPump>(`/espnow/controllers/${controllerId}/master-pumps`, data);
  return unwrap(res);
}

async function updateMasterPump(
  pumpId: string,
  data: { router_mac?: string; relay_index?: number; label?: string | null }
): Promise<MasterPump> {
  const res = await apiService.put<MasterPump>(`/espnow/master-pumps/${pumpId}`, data);
  return unwrap(res);
}

async function deleteMasterPump(pumpId: string): Promise<void> {
  await apiService.delete(`/espnow/master-pumps/${pumpId}`);
}

// ─── Router endpoints ─────────────────────────────────────────────────────────

async function getRouters(controllerId: string): Promise<EspNowRouter[]> {
  const res = await apiService.get<EspNowRouter[]>(`/espnow/controllers/${controllerId}/routers`);
  return unwrap(res);
}

async function getRelayStates(controllerId: string): Promise<RouterRelayState[]> {
  const res = await apiService.get<RouterRelayState[]>(`/espnow/controllers/${controllerId}/relay-states`);
  return unwrap(res);
}

/**
 * Latest device-reported schedule snapshot for every relay-capable node under
 * this controller. Nodes post these every 5 min, and immediately on config
 * apply / first time sync / re-registration.
 */
async function getScheduleState(controllerId: string): Promise<NodeScheduleState[]> {
  const res = await apiService.get<NodeScheduleState[]>(`/espnow/controllers/${controllerId}/schedule-state`);
  return unwrap(res);
}

async function updateRouterLocation(
  routerMac: string,
  controllerId: string,
  lat: number,
  lng: number
): Promise<EspNowRouter> {
  const res = await apiService.put<EspNowRouter>(`/espnow/routers/${encodeURIComponent(routerMac)}/location`, {
    lat, lng, controller_id: controllerId,
  });
  return unwrap(res);
}

/**
 * Save a node's relay labels, how many relays its board has, and which of them
 * are not in use.
 *
 * `relayCount` undefined leaves the stored value alone; null clears it back to
 * unknown so the transport default in `relayCountFor` applies again.
 *
 * `disabledRelays` undefined likewise leaves the stored list alone. It has no
 * null: `[]` is already the complete way to say every relay is in use.
 */
async function updateRouterLabels(
  routerMac: string,
  controllerId: string,
  labels: Record<string, string>,
  relayCount?: number | null,
  disabledRelays?: number[]
): Promise<EspNowRouter> {
  const res = await apiService.put<EspNowRouter>(`/espnow/routers/${encodeURIComponent(routerMac)}/labels`, {
    labels,
    controller_id: controllerId,
    ...(relayCount !== undefined ? { relay_count: relayCount } : {}),
    ...(disabledRelays !== undefined ? { disabled_relays: disabledRelays } : {}),
  });
  return unwrap(res);
}

async function updateRouterName(
  routerMac: string,
  controllerId: string,
  name: string
): Promise<EspNowRouter> {
  const res = await apiService.put<EspNowRouter>(`/espnow/routers/${encodeURIComponent(routerMac)}/name`, {
    name, controller_id: controllerId,
  });
  return unwrap(res);
}

// ─── Schedule program endpoints ───────────────────────────────────────────────

async function getPrograms(controllerId: string): Promise<ScheduleProgram[]> {
  const res = await apiService.get<ScheduleProgram[]>(`/espnow/controllers/${controllerId}/programs`);
  return unwrap(res);
}

async function createProgram(
  controllerId: string,
  data: { name: string; days_of_week: number[]; starts: ProgramStart[]; enabled?: boolean }
): Promise<ScheduleProgram> {
  const res = await apiService.post<ScheduleProgram>(`/espnow/controllers/${controllerId}/programs`, data);
  return unwrap(res);
}

async function updateProgram(
  programId: string,
  data: { name?: string; days_of_week?: number[]; starts?: ProgramStart[]; enabled?: boolean }
): Promise<ScheduleProgram> {
  const res = await apiService.put<ScheduleProgram>(`/espnow/programs/${programId}`, data);
  return unwrap(res);
}

async function deleteProgram(programId: string): Promise<void> {
  await apiService.delete(`/espnow/programs/${programId}`);
}

/**
 * Set one run time across every valve in the program.
 *
 * The schedule builder's default: a grower building a twelve-step sequence
 * wants "thirty minutes each", not thirty minutes typed twelve times. One call
 * rather than a PUT per relay, so a failure part-way cannot leave half the
 * program on the new time and half on the old.
 *
 * Returns the whole program tree with every relay and step duration as the
 * server now holds them.
 */
async function setProgramRelayDuration(programId: string, durationSec: number): Promise<ScheduleProgram> {
  const res = await apiService.put<ScheduleProgram>(
    `/espnow/programs/${programId}/relay-duration`,
    { duration_sec: durationSec },
  );
  return unwrap(res);
}

/**
 * Start a program right now.
 *
 * This MOVES the program's start time to the current moment rather than
 * queuing a one-off run, and the UI has to say so — a relay holds one schedule
 * slot evaluated on time-of-day every day, so "once" is not a thing the
 * hardware can express. The program keeps its new time until somebody changes
 * it, which is what restoreProgramSchedule undoes.
 *
 * Repeat presses are fine and always re-run: the only refusal is a program that
 * is watering at this moment (409, with the zone named).
 */
async function runProgramNow(programId: string): Promise<RunNowResult> {
  const res = await apiService.post<RunNowResult>(`/espnow/programs/${programId}/run-now`, {});
  return unwrap(res);
}

/** Put back the start time and days that the most recent "Run now" replaced. */
async function restoreProgramSchedule(programId: string): Promise<ScheduleProgram> {
  const res = await apiService.post<ScheduleProgram>(
    `/espnow/programs/${programId}/restore-schedule`, {}
  );
  return unwrap(res);
}

// ─── Step endpoints ───────────────────────────────────────────────────────────

async function addStep(programId: string): Promise<ScheduleStep> {
  const res = await apiService.post<ScheduleStep>(`/espnow/programs/${programId}/steps`, {});
  return unwrap(res);
}

async function updateStep(stepId: string, data: { step_order?: number }): Promise<ScheduleStep> {
  const res = await apiService.put<ScheduleStep>(`/espnow/steps/${stepId}`, data);
  return unwrap(res);
}

async function deleteStep(stepId: string): Promise<void> {
  await apiService.delete(`/espnow/steps/${stepId}`);
}

// ─── Step relay endpoints ─────────────────────────────────────────────────────

async function addRelay(
  stepId: string,
  data: { router_mac: string; relay_index: number; duration_sec: number; master_pump_id?: string | null }
): Promise<StepRelay> {
  const res = await apiService.post<StepRelay>(`/espnow/steps/${stepId}/relays`, data);
  return unwrap(res);
}

async function updateRelay(
  relayId: string,
  data: { router_mac?: string; relay_index?: number; duration_sec?: number; master_pump_id?: string | null }
): Promise<StepRelay> {
  const res = await apiService.put<StepRelay>(`/espnow/step-relays/${relayId}`, data);
  return unwrap(res);
}

async function deleteRelay(relayId: string): Promise<void> {
  await apiService.delete(`/espnow/step-relays/${relayId}`);
}

// ─── Events & Commands ────────────────────────────────────────────────────────

async function getEvents(controllerId: string, limit = 100, offset = 0): Promise<EspNowEvent[]> {
  const res = await apiService.get<EspNowEvent[]>(`/espnow/controllers/${controllerId}/events`, {
    params: { limit, offset },
  });
  return unwrap(res);
}

/**
 * One command we sent, and what the site said back.
 *
 * `status` is the site's verdict, not ours: QUEUED means it has not collected
 * the command yet, DISPATCHED that it has and has not answered, and OK /
 * CONFIRMED / ERROR are its own words. `detail` is the sentence it sent with a
 * refusal, and is very often the whole diagnosis — "unsupported command type
 * 'RUN_PROGRAM'" says update the bridge, and nothing else on the page does.
 */
export interface FieldCommand {
  cmd_id: string;
  type: string;
  payload: Record<string, unknown>;
  status: 'QUEUED' | 'DISPATCHED' | 'OK' | 'CONFIRMED' | 'PENDING' | 'ERROR';
  status_code: number | null;
  /** The difference between "stored" and "will actually water". */
  armed: boolean | null;
  detail: string | null;
  created_at: string;
  dispatched_at: string | null;
  confirmed_at: string | null;
}

/** Recent commands sent to this controller, newest first. */
async function getCommandLog(controllerId: string, limit = 50): Promise<FieldCommand[]> {
  const res = await apiService.get<FieldCommand[]>(`/espnow/controllers/${controllerId}/commands`, {
    params: { limit },
  });
  return unwrap(res);
}

async function sendCommand(
  controllerId: string,
  command: { type: string; router_mac?: string; relay?: number; start_sec?: number; duration_sec?: number }
): Promise<{ cmd_id: string }> {
  const res = await apiService.post<{ cmd_id: string }>(`/espnow/controllers/${controllerId}/command`, command);
  return unwrap(res);
}

async function queueOtaUpdate(
  controllerId: string,
  target: 'master' | 'router',
  routerMac?: string
): Promise<{ cmd_id: string }> {
  const body: { target: 'master' | 'router'; router_mac?: string } = { target };
  if (target === 'router' && routerMac) body.router_mac = routerMac;
  const res = await apiService.post<{ cmd_id: string }>(`/espnow/controllers/${controllerId}/ota`, body);
  return unwrap(res);
}

async function pushSchedule(controllerId: string): Promise<{ queued_at: string }> {
  const res = await apiService.post<{ queued_at: string }>(`/espnow/controllers/${controllerId}/push-schedule`, {});
  return unwrap(res);
}

export interface PushAckCommandDetail {
  type: string;
  status: string;
  status_code: number | null;
  armed: boolean | null;
  detail: string | null;
  router_mac: string | null;
  relay: number | null;
}

export interface PushAckStatus {
  total: number;
  confirmed: number;
  dispatched: number;
  queued: number;
  error: number;
  /**
   * Commands the device confirmed but reported it cannot act on (`armed:
   * false` — no clock, or zero duration). Deliberately counted separately from
   * both `confirmed` and `error`: delivered successfully, will not water.
   */
  unarmed?: number;
  details?: PushAckCommandDetail[];
}

async function getPushAckStatus(controllerId: string, since: string): Promise<PushAckStatus> {
  const res = await apiService.get<PushAckStatus>(`/espnow/controllers/${controllerId}/push-ack-status`, {
    params: { since },
  });
  return unwrap(res);
}

// ─── Stop watering, and the hold that makes it stick ──────────────────────────

/**
 * How far a stop got.
 *
 *   pumps      pump OFF sent, waiting for the site to confirm it
 *   valves     pumps confirmed off; the valves are being closed
 *   done       everything is off
 *   stalled    a pump did NOT confirm, and its valves are still open ON PURPOSE
 *   delegated  handed to an XBee bridge, which owns the ordering at the site
 *   failed     could not be carried out (see `detail`)
 *
 * `stalled` is not a kind of `failed` and must never be rendered as one. The
 * site did exactly the right thing: a pump that will not acknowledge its OFF
 * may still be turning, and the open valves are the only path it has. The
 * screen's job there is to say so and send somebody to the pump.
 */
export type StopPhase = 'pumps' | 'valves' | 'done' | 'stalled' | 'delegated' | 'failed';

export interface StopRelay {
  router_mac: string;
  relay_index: number;
  label: string;
  is_pump: boolean;
  /** Only meaningful for pumps: the site has reported the relay actually clear. */
  confirmed?: boolean;
}

export interface StopRequest {
  id: string;
  requested_at: string;
  requested_by: string | null;
  phase: StopPhase;
  detail: string | null;
  reason: string | null;
  pumps: StopRelay[];
  valves: StopRelay[];
  finished_at: string | null;
}

export interface WateringStopStatus {
  controller_id: string;
  transport: string;
  /** The hold. Outlives the request that created it — that is the whole point. */
  paused: boolean;
  paused_at: string | null;
  paused_by: string | null;
  pause_reason: string | null;
  /** The most recent stop, or null if this site has never had one. */
  request: StopRequest | null;
}

/**
 * Shut this site down now: pumps first, confirmed, then everything else.
 *
 * Returns as soon as the first commands are away, NOT when the site is quiet —
 * the pump confirmation is a radio round trip and on an XBee site the whole
 * sequence is minutes. Poll getStopStatus for the rest.
 */
async function stopWatering(controllerId: string, reason?: string): Promise<WateringStopStatus> {
  const res = await apiService.post<WateringStopStatus>(
    `/espnow/controllers/${controllerId}/stop`,
    { reason: reason || null }
  );
  return unwrap(res);
}

/** Where the last stop got to, and whether the site is still held. */
async function getStopStatus(controllerId: string): Promise<WateringStopStatus> {
  const res = await apiService.get<WateringStopStatus>(`/espnow/controllers/${controllerId}/stop-status`);
  return unwrap(res);
}

/**
 * Lift the hold and hand the site back to its schedule.
 *
 * Needs an irrigation *admin* seat where stopping needs none. Deciding it is
 * safe to water again is a different judgement from deciding to stop.
 */
async function resumeWatering(controllerId: string): Promise<WateringStopStatus> {
  const res = await apiService.post<WateringStopStatus>(`/espnow/controllers/${controllerId}/resume`, {});
  return unwrap(res);
}

// ─── Farm assignment ──────────────────────────────────────────────────────────

async function updateControllerFarm(controllerId: string, farmId: number | null): Promise<EspNowController> {
  const res = await apiService.put<EspNowController>(`/espnow/controllers/${controllerId}/farm`, { farm_id: farmId });
  return unwrap(res);
}

// ─── Relay zone endpoints ─────────────────────────────────────────────────────

/**
 * The drawn outlines for one controller, without the day's timeline.
 *
 * `getLiveMap` returns these too, but it carries every program step with them —
 * far too much to fetch just to say which area a valve waters.
 */
async function getRelayZones(controllerId: string): Promise<RelayZone[]> {
  const res = await apiService.get<RelayZone[]>(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/zones`
  );
  return unwrap(res);
}

async function updateRelayZone(
  routerMac: string,
  relayIndex: number,
  controllerId: string,
  data: {
    zone_name?: string | null;
    zone_color?: string;
    boundary_geometry?: GeoJSONPolygon | null;
    notes?: string | null;
  }
): Promise<RelayZone> {
  const res = await apiService.put<RelayZone>(
    `/espnow/routers/${encodeURIComponent(routerMac)}/relays/${relayIndex}/zone`,
    { controller_id: controllerId, ...data }
  );
  return unwrap(res);
}

async function deleteRelayZone(routerMac: string, relayIndex: number, controllerId: string): Promise<void> {
  await apiService.delete(
    `/espnow/routers/${encodeURIComponent(routerMac)}/relays/${relayIndex}/zone?controller_id=${encodeURIComponent(controllerId)}`
  );
}

// ─── Live map ─────────────────────────────────────────────────────────────────

async function getLiveMap(controllerId: string): Promise<LiveMapData> {
  const res = await apiService.get<LiveMapData>(`/espnow/controllers/${controllerId}/live-map`);
  return unwrap(res);
}

// ─── Admin endpoints ──────────────────────────────────────────────────────────

// ─── Mesh diagnostics (XBee) ─────────────────────────────────────────────────
// Each of these queues a command; the bridge runs it against the mesh and the
// results arrive through the endpoints that already exist - discovered nodes
// and RSSI via the routers upsert, pin state via relay-state, the neighbour map
// as a MESH_TOPOLOGY event. So the UI fires one of these, then refetches.

export type DiagnosticAction =
  | 'CHECK_COORDINATOR'
  | 'DISCOVER_NODES' | 'READ_STATES' | 'ALL_OFF' | 'READ_SIGNAL'
  | 'SET_NODE_NAME' | 'SCAN_MESH' | 'SET_BROADCASTING'
  /**
   * Create a node from the 64-bit address printed on the radio, with no
   * discovery involved. Not a question put to the mesh: it is the path that
   * still works when the mesh is not answering, which is exactly when an
   * installer is standing at the site unable to commission a node.
   */
  | 'ADD_NODE';

/**
 * How one queued diagnostic ended.
 *
 * `tone` comes from the server rather than being derived here, because "still
 * QUEUED" means different things at different ages: the bridge polls every ~15s,
 * so a command untouched three minutes later is stuck rather than pending, and
 * showing both as a spinner is what made these buttons look inert.
 */
export interface DiagnosticResult {
  cmd_id: string;
  action: DiagnosticAction;
  status: 'QUEUED' | 'DISPATCHED' | 'CONFIRMED' | 'OK' | 'ERROR';
  detail: string | null;
  created_at: string;
  dispatched_at: string | null;
  confirmed_at: string | null;
  router_mac: string | null;
  /**
   * How many nodes the command named, or null when it was site-wide - in which
   * case the bridge answered it against its OWN node list, not this
   * controller's. `coverageNote` exists because those two can diverge.
   *
   * Optional: absent from an older backend, where `router_mac` stands in.
   */
  scope_count?: number | null;
  tone: 'success' | 'error' | 'pending' | 'stale';
}

export interface MeshLink {
  from: string;
  /** A node address, or the literal 'coordinator' for a link to the radio. */
  to: string;
  /** The neighbour's NI, when the node reported one. */
  to_name?: string | null;
  /** Null when the node named a neighbour without reporting a signal for it. */
  rssi_dbm: number | null;
  quality: 'excellent' | 'good' | 'fair' | 'poor' | null;
}

export interface MeshTopology {
  scanned_at: string;
  scanned: number;
  links: MeshLink[];
  /**
   * How the map was built, in descending order of what it can tell you:
   *
   *   neighbour-table     FN — real links, each with its own signal strength.
   *   route-records       the hops the network layer actually uses, learned by
   *                       triggering many-to-one routing. Real topology, but
   *                       no per-link signal: every `rssi_dbm` is null.
   *   coordinator-signal  no topology at all — each node's signal to the
   *                       coordinator, which is a star, not a mesh.
   *
   * The distinction has to reach the operator: all three look identical in a
   * table of links.
   */
  method?: 'neighbour-table' | 'route-records' | 'coordinator-signal';
}

async function runDiagnostic(
  controllerId: string,
  body: {
    action: DiagnosticAction;
    router_mac?: string;
    router_macs?: string[];
    name?: string;
    enabled?: boolean;
    interval_ms?: number;
    relay_count?: number;
    /**
     * DISCOVER_NODES only. Raises the radio's discovery timeout (NT) to its
     * maximum for one sweep, then restores it. NT is the window every node
     * staggers its reply across, so a slow mesh several hops deep needs a long
     * one - and a long one is not the default because NT changes how the radio
     * behaves for everything else.
     */
    deep?: boolean;
  },
): Promise<{ cmd_id: string; action: string }> {
  const res = await apiService.post<{ cmd_id: string; action: string }>(
    `/espnow/controllers/${controllerId}/diagnostics`, body);
  return unwrap(res);
}

/** The last few diagnostics and what the bridge said about each. */
async function getDiagnosticResults(
  controllerId: string, limit = 10,
): Promise<DiagnosticResult[]> {
  const res = await apiService.get<DiagnosticResult[]>(
    `/espnow/controllers/${controllerId}/diagnostic-results?limit=${limit}`);
  return unwrap(res) ?? [];
}

/**
 * What the bridge's last inventory found.
 *
 * DISCOVER_NODES stopped being "an ND" - a broadcast cannot be made reliable,
 * so the bridge now answers from a unicast roll call over the nodes it already
 * knows, a multi-pass broadcast for ones it does not, and the addresses it has
 * heard passively since the last run. Those halves fail independently and need
 * different responses, which is why this is structured rather than a count:
 * `missing` is somebody's flat battery or dead hop, `new` is a radio somebody
 * has just installed.
 *
 * The ack text says the same thing in a sentence. This is read instead so the
 * UI never has to parse prose to find out which node went quiet.
 */
export interface NodeInventory {
  /** How many nodes the site knew about before this run. */
  known: number;
  answered: string[];
  /** Known nodes that answered nothing at all. The actionable list. */
  missing: string[];
  /** Addresses enrolled by this run. */
  new: string[];
  candidates: string[];
  passes: number;
  window_sec: number;
  detail: string;
  timestamp: string;
}

/**
 * A 64-bit address heard on this site's PAN that is not one of its nodes.
 *
 * Only discovery may create a node, so the bridge records these instead of
 * adopting them - which is what lets discovery search over HOURS rather than
 * over one ten-second window. A node that missed every broadcast is still
 * transmitting, and `source` says how it was noticed: `node_id` means it
 * announced itself (a rejoin or a commissioning button press, so it carries a
 * name), `traffic` means it was simply overheard.
 */
export interface NodeCandidate {
  router_mac: string;
  name: string | null;
  source: string | null;
  /** Why it spoke, when it announced itself rather than being overheard. */
  detail: string | null;
  timestamp: string;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function macList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** The most recent inventory, or null on a site where none has run yet. */
async function getNodeInventory(controllerId: string): Promise<NodeInventory | null> {
  const events = await getEvents(controllerId, 100);
  const row = events.find(e => e.event_type === 'NODE_INVENTORY');
  if (!row) return null;
  const p = row.payload || {};
  return {
    known: Number(p.known) || 0,
    answered: macList(p.answered),
    missing: macList(p.missing),
    new: macList(p.new),
    candidates: macList(p.candidates),
    passes: Number(p.passes) || 0,
    window_sec: Number(p.window_sec) || 0,
    detail: str(p.detail) ?? '',
    timestamp: row.timestamp,
  };
}

/**
 * Addresses worth offering to adopt.
 *
 * The bridge emits NODE_CANDIDATE once per address, so the event stays in the
 * history after the node has been added - filtering against the current node
 * list is what stops an adopted node being offered forever. Deduplicated
 * newest-first for the same reason: one address, one row.
 */
async function getNodeCandidates(
  controllerId: string, knownMacs: string[] = [],
): Promise<NodeCandidate[]> {
  const events = await getEvents(controllerId, 200);
  const adopted = new Set(knownMacs.map(m => m.toUpperCase()));
  const seen = new Set<string>();
  const out: NodeCandidate[] = [];

  for (const e of events) {
    if (e.event_type !== 'NODE_CANDIDATE') continue;
    const mac = (str(e.payload?.router_mac) ?? e.router_mac ?? '').toUpperCase();
    if (!mac || adopted.has(mac) || seen.has(mac)) continue;
    seen.add(mac);
    out.push({
      router_mac: mac,
      name: str(e.payload?.name),
      source: str(e.payload?.source),
      detail: str(e.payload?.detail),
      timestamp: e.timestamp,
    });
  }
  return out;
}

/**
 * Remove a node for good — replaced, decommissioned, or an address adopted by
 * mistake.
 *
 * Rejects with 409 when a program still waters it or a master pump is configured
 * on it; the error message names what to repoint first. That is deliberate: a
 * schedule left pointing at a node nobody owns silently stops watering that
 * block, and the bridge's pre-flight then reports a problem every night naming a
 * node that is no longer in the list.
 *
 * On an XBee site this also queues REMOVE_NODE, because the bridge keeps its own
 * copy of the node list and would otherwise push the node straight back.
 */
async function deleteRouter(controllerId: string, routerMac: string): Promise<void> {
  await apiService.delete(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/routers/${encodeURIComponent(routerMac)}`);
}

/** null when no scan has ever run — that is a normal state, not an error. */
async function getMeshTopology(controllerId: string): Promise<MeshTopology | null> {
  const res = await apiService.get<MeshTopology | null>(
    `/espnow/controllers/${controllerId}/mesh-topology`);
  return unwrap(res);
}

// ─── Pairing codes (XBee laptop sites) ───────────────────────────────────────
// An ESP-NOW controller is claimed by pre-registering its MAC, which is printed
// on the board. An XBee site has no such number to hand: its identity is the
// coordinator's 64-bit address, which nobody reads off a USB stick. So the flow
// inverts - we mint a code, the installer presents it along with the address it
// read over serial, and the server issues the API key.

/** A code as listed. `code` is raw; `display_code` is the ABCD-EFGH form. */
export interface PairingCode {
  code: string;
  display_code: string;
  farm_id: number | null;
  farm_name: string | null;
  controller_name: string | null;
  transport: string;
  controller_id: string | null;
  claimed_at: string | null;
  expires_at: string;
  created_at: string;
  active: boolean;
}

/** What you get back when minting one. Shown to the operator exactly once. */
export interface NewPairingCode {
  code: string;
  display_code: string;
  expires_at: string;
}

async function createPairingCode(params: {
  farm_id?: number | null;
  controller_name?: string | null;
  transport?: string;
  ttl_minutes?: number;
}): Promise<NewPairingCode> {
  const res = await apiService.post<NewPairingCode>('/espnow/pairing-codes', params);
  return unwrap(res);
}

async function listPairingCodes(): Promise<PairingCode[]> {
  const res = await apiService.get<PairingCode[]>('/espnow/pairing-codes');
  return unwrap(res);
}

async function revokePairingCode(code: string): Promise<void> {
  const res = await apiService.delete(`/espnow/pairing-codes/${encodeURIComponent(code)}`);
  unwrap(res);
}

// ─── Bridge releases ──────────────────────────────────────────────────────────

/** Newest published bridge build, or null if CI has not pushed one yet. */
async function getLatestBridgeRelease(): Promise<BridgeRelease | null> {
  const res = await apiService.get<BridgeRelease | null>('/v1/bridge/releases/latest');
  return unwrap(res);
}

async function listBridgeReleases(): Promise<BridgeRelease[]> {
  const res = await apiService.get<BridgeRelease[]>('/v1/bridge/releases');
  return unwrap(res);
}

/**
 * Fetch the installer for a first install on a farm laptop.
 *
 * Goes through raw axios rather than apiService because the response is bytes,
 * not the usual JSON envelope. The endpoint still requires the admin's session,
 * so the token is attached by hand here.
 */
async function downloadBridgeInstaller(): Promise<Blob> {
  const token = getToken();
  const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
  try {
    const response = await axios.get(`${baseURL}/v1/bridge/releases/latest/installer`, {
      responseType: 'blob',
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data as Blob;
  } catch (err: unknown) {
    // An error response to a blob request arrives as a Blob too, so the real
    // message is inside it rather than on err.response.data.
    const response = (err as { response?: { data?: unknown } }).response;
    if (response?.data instanceof Blob) {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        throw new Error(json?.error?.message || json?.error || 'Download failed.');
      } catch (parseErr) {
        if (parseErr instanceof SyntaxError) throw new Error('Download failed.');
        throw parseErr;
      }
    }
    throw err;
  }
}

/** Queue an UPDATE_BRIDGE command. Omit `version` to push the newest release. */
async function updateBridge(
  controllerId: string,
  version?: string
): Promise<{ cmd_id: string; version: string; sha256: string }> {
  const res = await apiService.post<{ cmd_id: string; version: string; sha256: string }>(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/update-bridge`,
    version ? { version } : {}
  );
  return unwrap(res);
}

async function getBridgeUpdateStatus(controllerId: string): Promise<BridgeUpdateStatus | null> {
  const res = await apiService.get<BridgeUpdateStatus | null>(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/bridge-update-status`
  );
  return unwrap(res);
}

async function adminGetAllControllers(): Promise<EspNowController[]> {
  const res = await apiService.get<EspNowController[]>('/espnow/admin/controllers');
  return unwrap(res);
}

async function adminAddController(controllerId: string): Promise<EspNowController> {
  const res = await apiService.post<EspNowController>('/espnow/admin/controllers', { controller_id: controllerId });
  return unwrap(res);
}


async function adminUnclaimController(controllerId: string): Promise<EspNowController> {
  const res = await apiService.post<EspNowController>(`/espnow/admin/controllers/${controllerId}/unclaim`, {});
  return unwrap(res);
}

async function adminDeleteController(controllerId: string): Promise<void> {
  await apiService.delete(`/espnow/admin/controllers/${controllerId}`);
}

// ─── Utility ──────────────────────────────────────────────────────────────────

/** Convert start_time_sec (seconds from midnight) to HH:MM string */
export function secToHHMM(sec: number): string {
  const h = Math.floor(sec / 3600).toString().padStart(2, '0');
  const m = Math.floor((sec % 3600) / 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

// ─── Sensor setup ────────────────────────────────────────────────────────────

import type { SensorDevice, SensorChannel } from './sensorDevices';
import type { NumericValue } from '@/utils/numeric';

export interface ControllerSensorData extends SensorDevice {
  /** The ESP-NOW node this device reads from. null = the master's own inputs. */
  field_node_mac: string | null;
  /** Joined from espnow_routers for labelling; null for the master. */
  router_name?: string | null;
  router_device_type?: string | null;
  router_online?: boolean | null;
  channels: SensorChannel[];
  latestReadings: Array<{
    id: number;
    reading_time: string;
    value: number | null;
    raw: Record<string, unknown> | null;
    channel_index: number;
    channel_kind: string;
  }>;
}

// Sensor devices are per-node: every call below takes an optional routerMac
// naming the ESP-NOW router to read from. Pass null/undefined for the master
// controller's own on-board inputs.
function routerMacQuery(routerMac?: string | null): string {
  return routerMac ? `?router_mac=${encodeURIComponent(routerMac)}` : '';
}

async function setupSensor(
  controllerId: string,
  routerMac?: string | null
): Promise<SensorDevice> {
  const res = await apiService.post<SensorDevice>(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/setup-sensor`,
    { router_mac: routerMac ?? null }
  );
  return unwrap(res);
}

/** Every sensor device on this controller — the master plus each router. */
async function listSensorsForController(controllerId: string): Promise<ControllerSensorData[]> {
  const res = await apiService.get<ControllerSensorData[]>(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/sensors`
  );
  return unwrap(res) ?? [];
}

async function getSensorForController(
  controllerId: string,
  routerMac?: string | null
): Promise<ControllerSensorData | null> {
  const res = await apiService.get<ControllerSensorData | null>(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/sensor${routerMacQuery(routerMac)}`
  );
  return unwrap(res);
}

async function upsertSensorChannel(
  controllerId: string,
  kind: 'analog' | 'digital',
  index: number,
  data: { type_key: string; name?: string | null; config?: Record<string, unknown>; enabled?: boolean },
  routerMac?: string | null
): Promise<SensorChannel> {
  const res = await apiService.put<SensorChannel>(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/sensor-channels/${kind}/${index}`,
    { ...data, router_mac: routerMac ?? null }
  );
  return unwrap(res);
}

async function deleteSensorChannel(
  controllerId: string,
  kind: 'analog' | 'digital',
  index: number,
  routerMac?: string | null
): Promise<void> {
  await apiService.delete(
    `/espnow/controllers/${encodeURIComponent(controllerId)}/sensor-channels/${kind}/${index}${routerMacQuery(routerMac)}`
  );
}

/** Convert HH:MM string to seconds from midnight */
export function hhmmToSec(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 3600 + (m || 0) * 60;
}

/** Format seconds duration as "Xh Ym" or "Ys" */
export function formatDuration(sec: number): string {
  if (sec >= 3600) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  if (sec >= 60) return `${Math.floor(sec / 60)}m`;
  return `${sec}s`;
}

/** Get relay state from bitmask */
export function getRelayState(bitmask: number, relayIndex: number): boolean {
  return Boolean((bitmask >> relayIndex) & 1);
}

/** Relays addressable through the 8-bit `relay_states` bitmask. */
export const MAX_RELAYS = 8;

/**
 * Relays to render for one node.
 *
 * `relay_count` is the answer whenever there is one: ESP-NOW firmware reports
 * it in MSG_ANNOUNCE, and an operator can set it by hand on the node card.
 * When it is null nobody has said, and the fallback has to be a guess — so make
 * it the guess that is right for the hardware:
 *
 * - **XBee is 4.** An XBee node is discovered over ND, which carries no IO
 *   capability at all, so it will never report a count on its own. The boards
 *   these sites run are 4-relay, and the bridge already assumes 4 when it
 *   drives them (`all_off`, the `SET_RELAY` default). Showing 8 invented four
 *   valves per node that do not physically exist.
 * - **Everything else is 8**, the bitmask width, which is what a node on
 *   pre-capability firmware was always shown as. Falling back to fewer there
 *   would make working relays disappear.
 *
 * Capped at MAX_RELAYS regardless: a relay with no bit in the state mask cannot
 * be reported on, so rendering one would be a control that never reads back.
 */
export function relayCountFor(
  node: { relay_count?: number | null; transport?: string | null } | null | undefined,
  // Only needed for shapes that do not carry their own transport - the farm
  // overview's OverviewRouter, where it hangs off the controller instead.
  transport?: string | null,
): number {
  const reported = node?.relay_count;
  if (typeof reported === 'number' && reported > 0) return Math.min(reported, MAX_RELAYS);
  return (transport ?? node?.transport) === 'xbee' ? 4 : MAX_RELAYS;
}

/**
 * A name the app generated for itself — "Relay 3" — rather than one anybody
 * chose. The zone editor used to prefill its name field with exactly this, so
 * plenty of stored zones carry one.
 */
export function isPlaceholderZoneName(name: string | null | undefined): boolean {
  return /^relay\s*\d+$/i.test((name ?? '').trim());
}

/**
 * What to call a valve.
 *
 * A zone name is an operator's own wording for the ground a valve waters, so it
 * outranks the node's relay label — except when it is one of those
 * placeholders. A zone mapped before its relay was labelled keeps "Relay 0"
 * forever, and letting that stale prefill win is how a valve labelled "1North"
 * on its node card reads as "Relay 0" on every page that draws its zone.
 *
 * The fallback is 0-based, matching the relay index the hardware uses and the
 * node card, the schedule builder and the rule editor all print. The landing
 * page used to count these from 1, so the same unnamed valve answered to two
 * different numbers depending on which page was open.
 */
export function relayDisplayName(
  zoneName: string | null | undefined,
  relayLabels: Record<string, string> | null | undefined,
  relayIndex: number,
  fallback = `Relay ${relayIndex}`,
): string {
  const zone = (zoneName ?? '').trim();
  if (zone && !isPlaceholderZoneName(zone)) return zone;
  const label = (relayLabels?.[String(relayIndex)] ?? '').trim();
  if (label) return label;
  return zone || fallback;
}

/**
 * The relay indices to OFFER for one node — its relays, less the ones an
 * operator has marked as wired to nothing.
 *
 * `relay_count` answers how many channels the board carries; it cannot answer
 * which of them go anywhere, and on a 4-relay board driving valves on 0 and 3
 * the two questions have different answers. Dropping the count to 2 would make
 * channel 3 unaddressable, so the unused ones are subtracted instead.
 *
 * Use this wherever a FUTURE choice is being made — the schedule picker, the
 * master-pump picker, the node's own relay grid. Do NOT use it to decide what
 * to draw in a live view: a relay marked out of use that still carries a
 * schedule row will still be driven by the hardware, and hiding a valve that is
 * actually open is a worse failure than showing one nobody wants. Those views
 * stay on `relayCountFor`.
 */
export function usableRelays(
  node: { relay_count?: number | null; transport?: string | null; disabled_relays?: number[] | null } | null | undefined,
  transport?: string | null,
): number[] {
  const off = new Set(node?.disabled_relays ?? []);
  return Array.from({ length: relayCountFor(node, transport) }, (_, i) => i)
    .filter(i => !off.has(i));
}

/**
 * Format a node-frame seconds-from-midnight value as HH:MM:SS.
 *
 * Deliberately not one of the `timezone.ts` helpers: `secs_today` and
 * `start_sec` are wall-clock offsets inside the *device's own* frame (UTC plus
 * the firmware's TIMEZONE_OFFSET_SEC), not instants. Re-projecting them into
 * the org timezone would invent an accuracy the payload does not carry.
 */
export function formatSecsOfDay(sec: number): string {
  const s = ((sec % 86400) + 86400) % 86400;
  const h = Math.floor(s / 3600).toString().padStart(2, '0');
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${h}:${m}:${ss}`;
}

/**
 * Signed difference (seconds) between the clock a node reported and the clock
 * it should have had at that instant, given the controller's timezone offset.
 * Returns null when there is nothing to compare against.
 *
 * A large value means the node will fire its programs at the wrong hour — a
 * failure a server-side clock would hide completely.
 */
export function clockSkewSec(
  secsToday: number | null,
  reportedAt: string | null,
  timezoneOffsetSec: number
): number | null {
  if (secsToday == null || !reportedAt) return null;
  const at = new Date(reportedAt);
  if (Number.isNaN(at.getTime())) return null;
  const utcSecs = at.getUTCHours() * 3600 + at.getUTCMinutes() * 60 + at.getUTCSeconds();
  const expected = (((utcSecs + timezoneOffsetSec) % 86400) + 86400) % 86400;
  let skew = secsToday - expected;
  // Fold into ±12 h so a midnight straddle doesn't read as a 24 h error.
  if (skew > 43200) skew -= 86400;
  if (skew < -43200) skew += 86400;
  return skew;
}

/** Compact "in 51m" / "in 2h 5m" for a next-run countdown. */
export function formatCountdown(sec: number): string {
  if (sec < 60) return `${Math.max(0, Math.round(sec))}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const espnowService = {
  getControllers,
  updateController,
  getMasterPumps,
  addMasterPump,
  updateMasterPump,
  deleteMasterPump,
  getRouters,
  getRelayStates,
  getScheduleState,
  updateRouterLocation,
  updateRouterLabels,
  updateRouterName,
  getPrograms,
  createProgram,
  updateProgram,
  setProgramRelayDuration,
  deleteProgram,
  runProgramNow,
  restoreProgramSchedule,
  addStep,
  updateStep,
  deleteStep,
  addRelay,
  updateRelay,
  deleteRelay,
  getEvents,
  getCommandLog,
  sendCommand,
  queueOtaUpdate,
  pushSchedule,
  getPushAckStatus,
  updateControllerFarm,
  stopWatering,
  getStopStatus,
  resumeWatering,
  getRelayZones,
  updateRelayZone,
  deleteRelayZone,
  getLiveMap,
  runDiagnostic,
  getDiagnosticResults,
  getMeshTopology,
  getNodeInventory,
  getNodeCandidates,
  deleteRouter,
  createPairingCode,
  listPairingCodes,
  revokePairingCode,
  getLatestBridgeRelease,
  listBridgeReleases,
  downloadBridgeInstaller,
  updateBridge,
  getBridgeUpdateStatus,
  adminGetAllControllers,
  adminAddController,
  adminUnclaimController,
  adminDeleteController,
  setupSensor,
  listSensorsForController,
  getSensorForController,
  upsertSensorChannel,
  deleteSensorChannel,
  getFarmAlertRecipients,
  setFarmAlertRecipients,
};

export default espnowService;
